/* app/onboarding/module1/Module1Overlay.jsx — Модуль 1 демо-CJM: квест-настройка
   ПОВЕРХ реального интерфейса (спотлайт/панель, замена модального мастера).
   Монтируется один раз в AppShell рядом с OnboardingOverlay (квестом).

   Источник правды — серверный OnboardingFlow (useAuth().user.onboarding_flow):
   авто-открытие когда флоу есть и не завершён; позиция = flow.current_step
   (resume после reload/relogin). На открытие флоу перечитывается с сервера.
   Закрытие (Позже / Esc / крестик) — session-dismiss; «шляпа студента» шлёт
   window-событие 'onb1:open' и переоткрывает с серверного шага.

   Механика шага скопирована с проверенного квеста (OnboardingOverlay):
   затемнение с «дыркой», rAF-слежение за target, clickThrough для реальных
   кликов, advance по кнопке/событию/появлению селектора/переходу URL.
   Отличия: z-index НИЖЕ квеста (966 < 970 — при активном квесте рендерим null),
   ensure-навигация на страницу шага при входе, деградация до центрированной
   карточки с кнопкой прямого перехода (fallbackNav), «Пропустить» у
   необязательных шагов. Все мутации флоу — authApi.patchOnboardingFlow
   ({ok,status,data}, best-effort: сбой сети не запирает пользователя).

   Тарифные разделы: если тариф активного пространства закрыл «Интеграции», шаги про кассу и
   данные не показываются — resolveM1Step прыгает на финал, а эффект ниже один раз фиксирует
   во флоу skipped (+ current_step: done). Тексты шагов и кнопку финала для пространства без
   «Аналитики» подменяет applySectionCopy (финал ведёт к Ассистенту, а не в закрытый раздел). */

import React from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../providers/AuthProvider.jsx';
import { useOnboarding } from '../OnboardingProvider.jsx';
import {
  M1_SLUGS, m1Session, firstStepForSlug, stepById, nextStepId, slugIndexOf, resolveM1Step,
} from './module1Steps.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { getOverlayRoot } from '../../../lib/overlay.jsx';
import * as authApi from '../../../lib/api/auth.js';
import { applySectionCopy, isSectionEnabled } from '../../../lib/planSections.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } = React;
  const { useNavigate, useLocation } = RR;

  function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  const advListOf = (step) =>
    (step && step.advance ? (Array.isArray(step.advance) ? step.advance : [step.advance]) : []);

  /* ── геометрия подсказки относительно «дырки» (как у квеста) ── */
  function placeTip(hole, placement, tw, th, vw, vh) {
    const GAP = 16, EDGE = 14;
    if (!hole) return { left: (vw - tw) / 2, top: (vh - th) / 2 };
    let pl = placement || 'auto';
    if (pl === 'auto') {
      const below = vh - (hole.y + hole.h), above = hole.y;
      const right = vw - (hole.x + hole.w), left = hole.x;
      const m = Math.max(below, above, right, left);
      pl = m === below ? 'bottom' : m === above ? 'top' : m === right ? 'right' : 'left';
    }
    let left, top;
    if (pl === 'top') { top = hole.y - th - GAP; left = hole.x + hole.w / 2 - tw / 2; }
    else if (pl === 'bottom') { top = hole.y + hole.h + GAP; left = hole.x + hole.w / 2 - tw / 2; }
    else if (pl === 'left') { left = hole.x - tw - GAP; top = hole.y + hole.h / 2 - th / 2; }
    else { left = hole.x + hole.w + GAP; top = hole.y + hole.h / 2 - th / 2; }
    left = Math.min(Math.max(EDGE, left), vw - tw - EDGE);
    top = Math.min(Math.max(EDGE, top), vh - th - EDGE);
    return { left, top };
  }

  // «Шаг N из 5» + мини-прогресс по линейке слагов
  function StepMeter({ slug }) {
    const idx = slugIndexOf(slug);
    const total = M1_SLUGS.length;
    const pct = Math.round(((idx + 1) / total) * 100);
    return (
      <div className="onb1-meter" aria-label={`Шаг ${idx + 1} из ${total}`}>
        <span className="onb1-meter__label t-caption">Шаг {idx + 1} из {total}</span>
        <span className="onb1-meter__track" aria-hidden="true">
          <span className="onb1-meter__fill" style={{ width: pct + '%' }} />
        </span>
      </div>
    );
  }

  /* ── спотлайт: затемнение с «дыркой» + карточка ── */
  function M1Spotlight({ step, busy, onAdvanceButton, onFallbackNav, onSkip, onClose }) {
    const [geo, setGeo] = useState(null);
    const tipRef = useRef(null);
    const lastKey = useRef('');

    const measure = useCallback(() => {
      const pad = step.pad != null ? step.pad : 8;
      const vw = window.innerWidth, vh = window.innerHeight;
      const q = (sel) => { if (!sel) return null; try { return document.querySelector(sel); } catch { return null; } };
      const rectOf = (el) => { if (!el) return null; const r = el.getBoundingClientRect(); return (r.width > 0 && r.height > 0) ? r : null; };
      let r = rectOf(q(step.target));
      let fallback = false;
      if (!r && step.fallbackTarget) { r = rectOf(q(step.fallbackTarget)); if (r) fallback = true; }
      let g;
      if (r) {
        const x = Math.max(0, r.left - pad), y = Math.max(0, r.top - pad);
        const w = Math.min(vw - x, r.width + pad * 2), h = Math.min(vh - y, r.height + pad * 2);
        g = { hole: { x, y, w, h }, vw, vh, fallback };
      } else {
        g = { hole: null, vw, vh, fallback: false };
      }
      const tip = tipRef.current;
      g.tip = placeTip(g.hole, step.placement, tip ? tip.offsetWidth : 340, tip ? tip.offsetHeight : 180, vw, vh);
      const key = JSON.stringify(g);
      if (key !== lastKey.current) { lastKey.current = key; setGeo(g); }
    }, [step.id, step.target, step.fallbackTarget]);

    useLayoutEffect(() => { measure(); }, [measure]);

    // rAF-слежение за элементом + подвод в зону видимости (как у квеста)
    useEffect(() => {
      let raf = 0;
      const loop = () => { measure(); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
      let el = null;
      try { el = document.querySelector(step.target) || (step.fallbackTarget && document.querySelector(step.fallbackTarget)); } catch { el = null; }
      if (el && el.scrollIntoView) {
        try { el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' }); } catch { el.scrollIntoView(); }
      }
      return () => cancelAnimationFrame(raf);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measure, step.id]);

    // фокус в карточку (клавиатура не теряется под затемнением)
    useEffect(() => {
      if (tipRef.current) { try { tipRef.current.focus({ preventScroll: true }); } catch { tipRef.current.focus(); } }
    }, [step.id]);

    const hole = geo && geo.hole;
    const vw = geo ? geo.vw : 0, vh = geo ? geo.vh : 0;
    const tip = geo && geo.tip ? geo.tip : { left: -9999, top: -9999 };
    const btnAdv = advListOf(step).find((a) => a.type === 'button');
    const degraded = !!geo && !hole;
    const hint = (geo && geo.fallback) ? (step.fallbackHint || step.hint) : step.hint;
    const dimCls = 'onb1-dim' + (step.clickThrough ? ' onb1-dim--through' : '');

    return (
      <div className="onb1">
        {hole ? (
          <>
            <div className={dimCls} style={{ left: 0, top: 0, width: '100%', height: hole.y }} />
            <div className={dimCls} style={{ left: 0, top: hole.y + hole.h, width: '100%', height: Math.max(0, vh - (hole.y + hole.h)) }} />
            <div className={dimCls} style={{ left: 0, top: hole.y, width: hole.x, height: hole.h }} />
            <div className={dimCls} style={{ left: hole.x + hole.w, top: hole.y, width: Math.max(0, vw - (hole.x + hole.w)), height: hole.h }} />
            <div className="onb1-ring" key={step.id} style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h }} />
          </>
        ) : (
          <div className={dimCls} style={{ inset: 0, width: '100%', height: '100%' }} />
        )}

        <div className="onb1-tip" key={'tip-' + step.id} ref={tipRef} tabIndex={-1}
          role="dialog" aria-label={step.title}
          style={{ left: tip.left, top: tip.top }}>
          <button type="button" className="onb1-tip__close" onClick={onClose} aria-label="Свернуть настройку">
            <Icon name="close" size={14} />
          </button>
          <StepMeter slug={step.slug} />
          <div className="onb1-tip__title">{step.title}</div>
          <div className="onb1-tip__body">{step.body}</div>
          <div className="onb1-tip__foot">
            {btnAdv ? (
              <Button variant="primary" size="sm" iconRight="arrow-right" loading={busy} disabled={busy}
                onClick={() => onAdvanceButton(btnAdv)}>{btnAdv.label || 'Дальше'}</Button>
            ) : degraded && step.fallbackNav ? (
              // target не найден (не та страница / узкая раскладка) — прямой переход
              <Button variant="primary" size="sm" iconRight="arrow-right" disabled={busy}
                onClick={() => onFallbackNav(step.fallbackNav)}>{step.fallbackLabel || 'Продолжить'}</Button>
            ) : (
              <span className="onb1-tip__hint"><span className="onb1-pulse" />{hint || 'Сделайте действие на подсвеченном элементе'}</span>
            )}
            {step.skip && (
              <button type="button" className="onb1-skip" disabled={busy} onClick={onSkip}>Пропустить</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── центрированные панели (welcome / finale) ── */
  function M1Panel({ step, busy, onPrimary, onLater }) {
    const ref = useRef(null);
    useEffect(() => {
      const el = ref.current && ref.current.querySelector('button.btn--primary, button');
      if (el) { try { el.focus({ preventScroll: true }); } catch { el.focus(); } }
    }, [step.id]);

    const isWelcome = step.panel === 'welcome';
    return (
      <div className="onb1 onb1--panel" ref={ref}>
        <div className="onb1-card" role="dialog" aria-modal="true" aria-label={step.title}>
          {/* На финале — «Шаг 5 из 5», чтобы завершение читалось полным даже
              когда команду/кассу пропустили (иначе перескок 3→«Готово» пугал). */}
          {!isWelcome && <StepMeter slug={step.slug} />}
          <div className={'onb1-card__badge' + (isWelcome ? '' : ' onb1-card__badge--ok')}>
            <Icon name={isWelcome ? 'sparkles' : 'check-circle'} size={22} />
          </div>
          <h2 className="onb1-card__title">{step.title}</h2>
          <p className="onb1-card__lead">{step.body}</p>
          {isWelcome && (
            <div className="onb1-card__meta"><Icon name="clock" size={14} /> несколько минут · можно прерваться и вернуться</div>
          )}
          <div className="onb1-card__actions">
            <Button variant="primary" size="lg" iconRight="arrow-right" loading={busy} disabled={busy} onClick={onPrimary}>
              {isWelcome ? 'Начать' : (step.primaryLabel || 'Показать Аналитику')}
            </Button>
            {isWelcome && (
              <Button variant="ghost" size="lg" onClick={onLater}>Позже</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ── движок ── */
  /* Поверх мастера открыли модальное окно — приостанавливаемся.

     Зачем. Шаг «Пригласите коллег» подсвечивает кнопку «Пригласить» и ждёт, пока
     приглашение реально создадут. По клику поверх открывается модальное окно, но
     подсветка продолжает следить за кнопкой, которая теперь ПОД окном: rAF честно
     отдаёт её прежние координаты, и «дырка» рисуется посреди чужой формы, указывая
     в пустоту, а карточка мастера ложится на поля ввода. Клиент оказывался зажат
     между двумя окнами и выбирался только через «Пропустить».

     Прятаться, а не перецеливаться: пока человек заполняет форму, подсказка ему не
     нужна — она вернётся, когда окно закроют, и доведёт шаг до конца. Состояние
     шага при этом не трогаем, поэтому «продолжим, как только приглашение создано»
     остаётся правдой.

     Ищем ЧУЖИЕ модалки: у самого мастера aria-modal нет, но проверку на .onb1
     оставляем на случай, если он им однажды обзаведётся. */
  function useModalAbove() {
    const [above, setAbove] = useState(false);
    useEffect(() => {
      const root = getOverlayRoot ? getOverlayRoot() : null;
      if (!root) return undefined;
      const check = () => {
        const found = [...root.querySelectorAll('[aria-modal="true"]')]
          .some((el) => !el.closest('.onb1, .onb1-tip'));
        setAbove(found);
      };
      check();
      const mo = new MutationObserver(check);
      mo.observe(root, { childList: true, subtree: true, attributes: true,
                         attributeFilter: ['aria-modal'] });
      return () => mo.disconnect();
    }, []);
    return above;
  }

  function Module1Overlay() {
    const { user, switchWorkspace, refreshUser, activeWorkspace } = useAuth();
    const quest = useOnboarding();
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [stepId, setStepId] = useState(null);
    const [flow, setFlow] = useState(null);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const autoRef = useRef(false);
    const flowRef = useRef(null);
    useEffect(() => { flowRef.current = flow; }, [flow]);

    // Флаги разделов примитивами (activeWorkspace меняет ссылку при каждом refreshWorkspaces):
    // шаг и его тексты пересчитываются только когда раздел реально открылся/закрылся.
    const analyticsOn = isSectionEnabled(activeWorkspace, 'analytics');
    const integrationsOn = isSectionEnabled(activeWorkspace, 'integrations');
    const gate = useMemo(
      () => ({ sections: { analytics: analyticsOn, integrations: integrationsOn } }),
      [analyticsOn, integrationsOn]);
    // resolved: шаги закрытого раздела пропущены (у нас — касса/данные → финал);
    // step — то, что реально рисуем, уже с текстами под закрытые разделы.
    const resolved = stepId ? resolveM1Step(stepId, gate) : null;
    const resolvedId = resolved && resolved.stepId;
    const skipSlug = resolved && resolved.skipSlug;
    const step = useMemo(
      () => (resolvedId ? applySectionCopy(stepById(resolvedId), gate) : null),
      [resolvedId, gate]);
    const questActive = !!(quest && quest.active);
    const visible = open && !!step && !questActive;
    const modalAbove = useModalAbove();

    // видимость для внешних потребителей (гейт автозапуска Модуля 2 в ChatShell)
    useEffect(() => {
      m1Session.open = visible;
      return () => { m1Session.open = false; };
    }, [visible]);

    // авто-открытие: флоу есть, не завершён, в этой сессии не закрывали
    useEffect(() => {
      const f = user && user.onboarding_flow;
      if (!f || f.completed) return undefined;
      setFlow((prev) => prev || f);
      if (m1Session.dismissed || autoRef.current) return undefined;
      autoRef.current = true;
      // лёгкая задержка — первый кадр приложения должен отрисоваться,
      // чтобы спотлайту было за что зацепиться
      const t = setTimeout(() => {
        setStepId((cur) => cur || firstStepForSlug(f.current_step));
        setOpen(true);
      }, 400);
      return () => clearTimeout(t);
    }, [user]);

    // переоткрытие из «шляпы студента»: снять session-dismiss, встать на серверный шаг
    useEffect(() => {
      const h = () => {
        m1Session.dismissed = false;
        const f = flowRef.current || (user && user.onboarding_flow);
        if (!f || f.completed) return;
        setFlow((prev) => prev || f);
        setStepId(firstStepForSlug(f.current_step));
        setOpen(true);
      };
      window.addEventListener('onb1:open', h);
      return () => window.removeEventListener('onb1:open', h);
    }, [user]);

    // на каждое открытие перечитываем флоу с сервера (актуальный шаг после
    // reload / возврата из оплаты / параллельной вкладки)
    useEffect(() => {
      if (!open) return undefined;
      let alive = true;
      (async () => {
        const r = await authApi.getOnboardingFlow();
        if (!alive || !r.ok || !r.data) return;
        const f = r.data.flow;
        if (!f) { setOpen(false); return; }
        setFlow(f);
        if (f.completed) { setOpen(false); setStepId(null); }
      })().catch(() => {});
      return () => { alive = false; };
    }, [open]);

    // единая точка мутаций: PATCH → локальная копия из ответа; сбой сети не запирает
    const doPatch = useCallback(async (payload) => {
      try {
        const r = await authApi.patchOnboardingFlow(payload);
        if (r.ok && r.data && r.data.flow) { setFlow(r.data.flow); return r.data.flow; }
      } catch { /* noop — best-effort */ }
      return null;
    }, []);

    // Вход в шаг закрытого тарифом раздела (касса/данные при integrations=false): рисовать его
    // нельзя (resolveM1Step выше уже показывает финал), а серверу надо сказать правду —
    // «касса пропущена», иначе resume после reload вернёт сюда же. Срабатывает один раз:
    // после setStepId шаг уже финальный, и skipSlug обнуляется.
    useEffect(() => {
      if (!open || !skipSlug || !resolvedId) return;
      doPatch({ add_skipped: resolved.skipSlug, current_step: 'done' });
      setStepId(resolvedId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, skipSlug, resolvedId]);

    const close = useCallback((dismiss) => {
      if (dismiss) m1Session.dismissed = true;
      setOpen(false);
    }, []);

    const goStep = useCallback((id) => {
      if (!id) { setOpen(false); setStepId(null); return; }
      setStepId(id);
    }, []);

    // единая точка перехода по сработавшему advance-условию
    const fireAdvance = useCallback(async (st, adv, detail) => {
      if (busyRef.current) return;
      busyRef.current = true; setBusy(true);
      try {
        if (adv.ws) {
          // создано пространство (onb:ws-created): привязать к флоу и переключить
          // активный ws тем же механизмом, что и WorkspaceSwitcher (useAuth)
          const d = detail || {};
          const wsId = d.workspaceId != null ? d.workspaceId : d.workspace_id;
          const payload = { add_completed: 'space', current_step: 'team' };
          if (wsId != null) payload.workspace_id = wsId;
          await doPatch(payload);
          if (wsId != null && switchWorkspace) {
            try { await switchWorkspace(wsId); } catch { /* noop */ }
          }
        } else if (adv.patch) {
          await doPatch(adv.patch);
        }
        if (adv.complete) {
          await doPatch({ complete: true });
          setOpen(false); setStepId(null);
          if (adv.nav) navigate(adv.nav);  // финал: открыть Аналитику ДО refresh
          if (refreshUser) { try { await refreshUser(); } catch { /* noop */ } }
          return;
        }
        if (adv.nav) navigate(adv.nav);
        goStep(adv.to || nextStepId(st.id));
      } finally {
        busyRef.current = false; setBusy(false);
      }
    }, [doPatch, switchWorkspace, refreshUser, navigate, goStep]);

    // «Пропустить» у необязательного шага
    const onSkip = useCallback(async () => {
      const st = step;
      if (!st || !st.skip || busyRef.current) return;
      busyRef.current = true; setBusy(true);
      try { await doPatch(st.skip.patch); goStep(st.skip.to); }
      finally { busyRef.current = false; setBusy(false); }
    }, [step, doPatch, goStep]);

    // ensure-навигация: при входе на шаг перейти на его страницу (один раз,
    // дальше пользователя не преследуем — деградация покажет кнопку перехода)
    useEffect(() => {
      if (!visible || !step || !step.ensure) return;
      const url = (location.pathname || '') + (location.search || '');
      if (!url.includes(step.ensure.match)) navigate(step.ensure.url);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, step && step.id]);

    // advance: window-события (onb:ws-created / onb:invited / onb:integration-active)
    useEffect(() => {
      if (!visible || !step) return undefined;
      const evs = advListOf(step).filter((a) => a.type === 'event');
      if (!evs.length) return undefined;
      const offs = evs.map((a) => {
        const h = (e) => { fireAdvance(step, a, e && e.detail); };
        window.addEventListener(a.name, h);
        return () => window.removeEventListener(a.name, h);
      });
      return () => offs.forEach((off) => off());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, step && step.id]);

    // advance: появление селектора в DOM
    useEffect(() => {
      if (!visible || !step) return undefined;
      const aps = advListOf(step).filter((a) => a.type === 'appear');
      if (!aps.length) return undefined;
      let done = false;
      const check = () => {
        if (done) return;
        for (const a of aps) {
          let el = null;
          try { el = document.querySelector(a.selector); } catch { el = null; }
          if (el) { done = true; clearInterval(t); fireAdvance(step, a); return; }
        }
      };
      const t = setInterval(check, 200);
      check();
      return () => clearInterval(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, step && step.id]);

    // advance: пользователь сам перешёл (URL содержит match)
    useEffect(() => {
      if (!visible || !step) return;
      const url = (location.pathname || '') + (location.search || '');
      const g = advListOf(step).find((a) => a.type === 'goto'
        && (a.gone ? !url.includes(a.match) : url.includes(a.match)));
      if (g) fireAdvance(step, g);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, step && step.id, location.pathname, location.search]);

    // Esc — мягкое закрытие (session-dismiss), «шляпа» переоткроет
    useEffect(() => {
      if (!visible) return undefined;
      const onKey = (e) => { if (e.key === 'Escape') close(true); };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [visible, close]);

    if (!visible) return null;
    // Чужое модальное окно поверх — молчим, пока его не закроют (см. useModalAbove).
    if (modalAbove) return null;

    let body;
    if (step.kind === 'spotlight') {
      body = <M1Spotlight key={step.id} step={step} busy={busy}
        onAdvanceButton={(adv) => fireAdvance(step, adv)}
        onFallbackNav={(url) => navigate(url)}
        onSkip={onSkip} onClose={() => close(true)} />;
    } else {
      // Финальная панель несёт primary-advance (complete+nav) — завершает флоу
      // без отдельного шага-принуждения; welcome просто идёт дальше по списку.
      body = <M1Panel step={step} busy={busy}
        onPrimary={() => (step.primary ? fireAdvance(step, step.primary) : goStep(nextStepId(step.id)))}
        onLater={() => close(true)} />;
    }

    // визуально-скрытый анонс для скринридера (смена шага)
    const announce = `${step.title || ''}. ${(step.kind === 'spotlight' ? (step.hint || step.body) : step.body) || ''}`;

    const root = getOverlayRoot ? getOverlayRoot() : document.body;
    return ReactDOM.createPortal(
      <>
        <div className="onb1-sr" aria-live="polite" role="status">{announce}</div>
        {body}
      </>,
      root
    );
  }

export { Module1Overlay };
